"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { notificationRepository } from "../../data/repositories/notification.repository";
import type { NotificationItem } from "../../types";
export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationRepository.getNotifications({
        limit: 15
      });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch {
      // Silently fail — bell degrades gracefully
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    // ponytail: legitimate fetch-on-mount + poll, not a derived-state effect; rule false-positives here
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 30_000);

    // Allow external triggers (e.g. test notification button) to force a refetch
    window.addEventListener("notification:refetch", fetchNotifications);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener("notification:refetch", fetchNotifications);
    };
  }, [fetchNotifications]);
  const markAsRead = useCallback(async (id: string) => {
    await notificationRepository.markAsRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      isRead: true
    } : n));
    setUnreadCount(c => Math.max(0, c - 1));
  }, []);
  const markAllAsRead = useCallback(async () => {
    await notificationRepository.markAllAsRead();
    setNotifications(prev_0 => prev_0.map(n_0 => ({
      ...n_0,
      isRead: true
    })));
    setUnreadCount(0);
  }, []);
  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
}